import React, { useCallback, useEffect, useState } from 'react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { getEventCoordinates } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { createPortal } from 'react-dom';
import theme from 'theme';

export interface DragReorderContextProps {
  enabled: boolean;
  columnTypes?: string[];
  onColumnReorder?: (source: string, target: string) => void;
  columnLabels?: Record<string, string>;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  children: React.ReactNode;
}

const DragReorderContext = ({
  enabled,
  columnTypes,
  onColumnReorder,
  columnLabels,
  onDragStart: onDragStartCallback,
  onDragEnd: onDragEndCallback,
  children,
}: DragReorderContextProps) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [cursorPos, setCursorPos] = useState<{ x: number; y: number } | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const id = String(event.active.id);
      setActiveId(id);
      const coords = getEventCoordinates(event.activatorEvent as Event);
      if (coords) setCursorPos(coords);
      onDragStartCallback?.();
    },
    [onDragStartCallback]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null);
      setCursorPos(null);
      onDragEndCallback?.();
      const { active, over } = event;
      if (over && active.id !== over.id && onColumnReorder) {
        onColumnReorder(String(active.id), String(over.id));
      }
    },
    [onColumnReorder, onDragEndCallback]
  );

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
    setCursorPos(null);
    onDragEndCallback?.();
  }, [onDragEndCallback]);

  useEffect(() => {
    if (!activeId) return;
    const onMove = (e: PointerEvent) => setCursorPos({ x: e.clientX, y: e.clientY });
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => window.removeEventListener('pointermove', onMove);
  }, [activeId]);

  if (!enabled || !onColumnReorder) {
    return <>{children}</>;
  }

  const activeLabel = activeId && columnLabels ? columnLabels[activeId] : undefined;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}>
      <SortableContext items={columnTypes ?? []} strategy={horizontalListSortingStrategy}>
        {children}
      </SortableContext>
      {/* Empty overlay keeps dnd-kit internals happy without interfering with our portal chip */}
      <DragOverlay dropAnimation={null} />
      {activeId && cursorPos && createPortal(
        <div
          style={{
            position: 'fixed',
            left: cursorPos.x,
            top: cursorPos.y,
            transform: 'translate(-50%, -50%)',
            zIndex: 9999,
            pointerEvents: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '5px 12px 5px 8px',
            borderRadius: 6,
            background: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            boxShadow: '0 6px 20px rgba(0,0,0,0.22)',
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '0.01em',
            cursor: 'grabbing',
            whiteSpace: 'nowrap',
            opacity: 0.93,
            userSelect: 'none',
          }}>
          <DragIndicatorIcon
            style={{
              fontSize: 14,
              opacity: 0.85,
              flexShrink: 0,
            }}
          />
          {activeLabel ?? activeId}
        </div>,
        document.body
      )}
    </DndContext>
  );
};

export default DragReorderContext;
